/**
 * Chart — thin wrapper over ReactECharts (echarts-for-react/core).
 *
 * Why this exists: TECHSTACK.md (task 157) locks ECharts as Kanjoy's
 * charting library. Callers pass the raw ECharts `option` object — the
 * primitive does not abstract over chart types. What it does centralize:
 *
 * 1. Tree-shaken ECharts registration: only the chart types and components
 *    actually used in the current app surface are pulled in (line, bar,
 *    tooltip, grid, dataZoom). Adding a new chart type is a registration
 *    edit here, not in every caller.
 * 2. Theming via design tokens: colors flow from src/styles/tokens.css
 *    through `useChartTheme()`, never as hardcoded hex literals in
 *    callers' option objects.
 * 3. A11y: ECharts renders to <canvas>, which axe treats as opaque, so
 *    the wrapper sets an aria-label on the canvas container plus a
 *    visually-hidden text fallback consumed by screen readers.
 *
 * Per task 157 §Out of scope: no theme provider, no preset palette
 * beyond what tokens already define, no chart-type abstractions.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import EChartsReactCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts-for-react/lib/types';
import styles from './Chart.module.css';

// Register the modules we use up-front. ECharts mutates a shared global
// registry, so registering twice is a no-op and registering at module
// scope (rather than inside the component) means the cost is paid once
// per page load instead of once per Chart instance.
echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  CanvasRenderer,
]);

/**
 * Resolved color/font tokens — what `useChartTheme()` returns and what
 * the wrapper feeds into ECharts' `color` / `textStyle` / `tooltip`
 * defaults. The shape stays narrow on purpose; if a caller needs more
 * tokens it can read them off `document.documentElement` itself rather
 * than grow this surface.
 */
export interface ChartTheme {
  /** Categorical palette in priority order (navy, gold, info, success, danger). */
  colors: readonly string[];
  /** Foreground text color for axis labels, legend, etc. */
  text: string;
  /** Muted text for secondary labels (axis tick text). */
  textMuted: string;
  /** Axis line / split-line color. */
  border: string;
  /** Tooltip background. */
  tooltipBg: string;
  /** Sans-serif font family. */
  fontFamily: string;
  /** Base font size in pixels (read from the document root). */
  fontSize: number;
}

// Fallback used only when there is no DOM to read tokens from (SSR — which
// task 157 §Out of scope explicitly forbids — or jsdom without document.
// documentElement). The empty strings let ECharts fall through to its own
// default palette rather than burn a Kanjoy-specific hex into source. In
// practice this branch never runs in production: tokens.css is already on
// the document by the time React renders the first Chart.
const FALLBACK_THEME: ChartTheme = {
  colors: [],
  text: '',
  textMuted: '',
  border: '',
  tooltipBg: '',
  fontFamily: 'sans-serif',
  fontSize: 14,
};

function readToken(cs: CSSStyleDeclaration, name: string, fallback: string): string {
  const v = cs.getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

/**
 * Resolve design-token CSS variables into concrete values. Reads them
 * from `document.documentElement` once per call. Returns the fallback
 * theme when there is no DOM (jsdom without a body, SSR — though we
 * are SPA-only per task 157).
 */
function resolveChartTheme(): ChartTheme {
  if (typeof document === 'undefined' || !document.documentElement) {
    return FALLBACK_THEME;
  }
  const cs = getComputedStyle(document.documentElement);
  // The categorical palette intentionally leans on brand + semantic
  // tokens; a chart asking for a sixth color should add a token, not
  // hardcode a hex here.
  const colors = [
    readToken(cs, '--color-navy', FALLBACK_THEME.colors[0]),
    readToken(cs, '--color-gold', FALLBACK_THEME.colors[1]),
    readToken(cs, '--color-info-500', FALLBACK_THEME.colors[2]),
    readToken(cs, '--color-success-500', FALLBACK_THEME.colors[3]),
    readToken(cs, '--color-danger-500', FALLBACK_THEME.colors[4]),
  ];
  const fontSizeRaw = readToken(cs, '--text-base', '0.875rem');
  // tokens.css uses rems; resolve against the document root font-size
  // so ECharts (which wants px) does not under/over-shoot when a user
  // has their browser zoom or a custom html font-size set.
  const rootFontSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  let fontSize = FALLBACK_THEME.fontSize;
  if (fontSizeRaw.endsWith('rem')) {
    fontSize = Math.round(parseFloat(fontSizeRaw) * rootFontSizePx);
  } else if (fontSizeRaw.endsWith('px')) {
    fontSize = Math.round(parseFloat(fontSizeRaw));
  }
  return {
    colors,
    text: readToken(cs, '--text-primary', FALLBACK_THEME.text),
    textMuted: readToken(cs, '--text-secondary', FALLBACK_THEME.textMuted),
    border: readToken(cs, '--border-default', FALLBACK_THEME.border),
    tooltipBg: readToken(cs, '--bg-surface', FALLBACK_THEME.tooltipBg),
    fontFamily: readToken(cs, '--font-sans', FALLBACK_THEME.fontFamily),
    fontSize,
  };
}

/**
 * Hook returning the resolved chart theme tokens. Reads tokens.css
 * once on mount via useState's lazy initializer; the returned object
 * is stable for the lifetime of the consuming component. Callers
 * building their own `option` objects use this to keep colors in
 * sync with the rest of the app.
 *
 * Token changes mid-session would require a remount of the consumer
 * to take effect; that is fine — we do not yet support live theme
 * switching, and adding a MutationObserver for that is out of scope
 * for task 157.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the Chart primitive intentionally exports both the component and its sibling useChartTheme hook from the same module; they are the same unit (theming + render) and splitting would force two imports per caller for no HMR benefit.
export function useChartTheme(): ChartTheme {
  // useState lazy initializer reads the DOM exactly once per consumer
  // mount, avoiding the cascading-render warning the alternative
  // `useEffect(() => setTheme(...))` shape produces.
  const [theme] = useState<ChartTheme>(() => resolveChartTheme());
  return theme;
}

interface ChartProps {
  /** Raw ECharts option object. The primitive merges in theme defaults. */
  option: EChartsOption;
  /** Pixel height for the chart canvas. The width tracks the parent. */
  height: number;
  /**
   * Required for the a11y gate. Becomes the `aria-label` on the canvas
   * container and seeds a visually-hidden text fallback. ECharts'
   * canvas is opaque to assistive tech without this.
   */
  ariaLabel: string;
  /**
   * Optional plain-text description of the chart contents — populates
   * the visually-hidden fallback so screen-reader users get more than
   * just the aria-label. Falls back to the aria-label when omitted.
   */
  textFallback?: string;
}

/**
 * Apply theme defaults to an ECharts option object. Caller-provided
 * keys win; this is a baseline, not an override.
 */
function withThemeDefaults(option: EChartsOption, theme: ChartTheme): EChartsOption {
  // The shape we receive is intentionally `any` (echarts-for-react's
  // EChartsOption alias). We treat it as a plain object record for
  // shallow merging and let ECharts validate the inner shape.
  const opt = option as Record<string, unknown>;
  return {
    color: opt.color ?? theme.colors,
    textStyle: {
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSize,
      color: theme.text,
      ...((opt.textStyle as Record<string, unknown>) ?? {}),
    },
    grid: {
      // Sensible default grid padding so axis labels and the legend do
      // not crash into the chart area. Callers may override.
      left: 48,
      right: 24,
      top: 48,
      bottom: 40,
      containLabel: false,
      ...((opt.grid as Record<string, unknown>) ?? {}),
    },
    tooltip: {
      backgroundColor: theme.tooltipBg,
      borderColor: theme.border,
      borderWidth: 1,
      textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: theme.fontSize - 1 },
      ...((opt.tooltip as Record<string, unknown>) ?? {}),
    },
    ...opt,
  };
}

export function Chart({ option, height, ariaLabel, textFallback }: ChartProps) {
  const theme = useChartTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Merge once per (option, theme) pair so React-ECharts can diff a
  // stable reference instead of a new object on every render.
  const themedOption = useMemo(() => withThemeDefaults(option, theme), [option, theme]);

  // ECharts paints into a <canvas>; axe needs the aria-label on the
  // wrapper to surface anything to assistive tech. Setting it imperatively
  // because EChartsReactCore types `aria-label` indirectly via DOM props.
  useEffect(() => {
    const el = containerRef.current?.querySelector('canvas');
    if (el) el.setAttribute('aria-label', ariaLabel);
  }, [ariaLabel, themedOption]);

  return (
    <div
      ref={containerRef}
      className={styles.wrapper}
      role="img"
      aria-label={ariaLabel}
      data-testid="chart"
    >
      <EChartsReactCore
        echarts={echarts}
        option={themedOption}
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
      <span className={styles.srOnly}>{textFallback ?? ariaLabel}</span>
    </div>
  );
}
